import { api } from "@/lib/api-client";

export interface LookupTableSummary {
  id: string;
  name: string;
  columns: string[];
  entryCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface LookupTableDetail extends LookupTableSummary {
  tenantId: string;
}

export interface LookupEntry {
  id: string;
  lookupTableId: string;
  key: string;
  data: Record<string, string>;
}

export interface LookupEntriesPage {
  entries: LookupEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface ImportResult {
  imported: number;
  columns: string[];
  skipped: number;
}

// List
export async function createLookupTable(input: {
  name: string;
  columns: string[];
}): Promise<LookupTableSummary> {
  return api.post<LookupTableSummary>("/lookup-tables", input);
}

// Detail
export async function getLookupTable(id: string): Promise<LookupTableDetail> {
  return api.get<LookupTableDetail>(`/lookup-tables/${id}`);
}

export async function updateLookupTable(
  id: string,
  input: Partial<{ name: string; columns: string[] }>,
): Promise<LookupTableDetail> {
  return api.patch<LookupTableDetail>(`/lookup-tables/${id}`, input);
}

export async function deleteLookupTable(id: string): Promise<void> {
  await api.delete<{ ok: boolean }>(`/lookup-tables/${id}`);
}

/**
 * R-76 — rename a single column using the dedicated endpoint.
 * PATCH /api/lookup-tables/:id/columns { from, to }
 * Returns the updated table (with new columns array).
 * Throws on 409 (duplicate_column_name), 422 (column_not_found),
 * 400 (empty to), and 404 (table not found / cross-tenant).
 */
export async function renameColumn(
  tableId: string,
  from: string,
  to: string,
): Promise<LookupTableDetail> {
  return api.patch<LookupTableDetail>(`/lookup-tables/${tableId}/columns`, {
    from,
    to,
  });
}

// Entries
export async function getLookupTableEntries(
  tableId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<LookupEntriesPage> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : "";
  return api.get<LookupEntriesPage>(`/lookup-tables/${tableId}/entries${query}`);
}

export async function createEntry(
  tableId: string,
  input: { key: string; data?: Record<string, string> },
): Promise<LookupEntry> {
  return api.post<LookupEntry>(`/lookup-tables/${tableId}/entries`, input);
}

export async function updateEntry(
  tableId: string,
  entryId: string,
  input: Partial<{ key: string; data: Record<string, string> }>,
): Promise<LookupEntry> {
  return api.patch<LookupEntry>(`/lookup-tables/${tableId}/entries/${entryId}`, input);
}

export async function deleteEntry(tableId: string, entryId: string): Promise<void> {
  await api.delete<{ ok: boolean }>(`/lookup-tables/${tableId}/entries/${entryId}`);
}

/**
 * Imports rows from a CSV into a lookup table.
 *
 * R-31 (2026-05-13) — `firstRowIsHeader` is sent as a query parameter
 * (not a multipart field) so that both the file-upload and paste-text
 * paths use the same wire format. The previous implementation packed
 * the flag into the FormData and used `api.post` (which JSON.stringifies
 * its body) — that meant the server received `"{}"` as the request body,
 * corrupted the table's columns to `["[object Object]"]`, and persisted
 * zero rows. See `apps/api/src/__tests__/lookup-tables.test.ts` "R-31"
 * regression tests.
 */
export async function importCsv(
  tableId: string,
  input: { file: File } | { text: string },
  options: { firstRowIsHeader: boolean },
): Promise<ImportResult> {
  const qs = `?firstRowIsHeader=${options.firstRowIsHeader ? "true" : "false"}`;
  const path = `/lookup-tables/${tableId}/entries/import-csv${qs}`;

  if ("file" in input) {
    const form = new FormData();
    form.append("file", input.file);
    return api.upload<ImportResult>(path, form);
  }

  return api.postRaw<ImportResult>(path, input.text, "text/csv");
}
